import { ChangeDetectionStrategy, Component, ElementRef, OnInit, effect, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputOtpModule } from 'primeng/inputotp';
import { AuthService } from '../../../core/services/auth.service';
import { PinVaultService } from '../../../core/services/pin-vault.service';
import { VerrouPosteAdmin } from '../../../core/services/verrou-poste';

const LONGUEUR_NIP = 6;

/**
 * Ecran du poste verrouille : plein ecran, au-dessus de tout, n'accepte que le NIP.
 *
 * Il reprend la carte de `/pin` (meme avatar, meme saisie `p-inputOtp` a 6 chiffres) pour que le geste
 * soit celui que la personne connait deja. Le deverrouillage est local (`VerrouPosteAdmin.deverrouiller`
 * ne touche pas a la session) : on reste sur la meme page, avec les memes saisies.
 *
 * Le coffre efface apres trop d'essais (limite `pinMaxAttempts`) deconnecte : il n'y a plus de NIP qui vaille.
 */
@Component({
  selector: 'app-ecran-verrou',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, ButtonModule, InputOtpModule],
  templateUrl: './ecran-verrou.component.html',
  styleUrl: './ecran-verrou.component.css',
})
export class EcranVerrouComponent implements OnInit {
  private readonly verrou = inject(VerrouPosteAdmin);
  private readonly auth = inject(AuthService);
  private readonly coffre = inject(PinVaultService);
  private readonly translate = inject(TranslateService);

  readonly longueur = LONGUEUR_NIP;
  nip = '';

  readonly nom = signal('');
  readonly initiale = signal('?');
  readonly verification = signal(false);
  readonly erreur = signal<string | null>(null);

  private readonly racine = viewChild.required<ElementRef<HTMLElement>>('racine');

  constructor() {
    // Le focus entre dans l'ecran : le clavier saisit le NIP, et le lecteur d'ecran l'annonce.
    effect(() => this.racine().nativeElement.focus());
  }

  async ngOnInit(): Promise<void> {
    const email = localStorage.getItem('currentUserEmail') ?? '';
    const meta = (await this.coffre.listVaults()).find((v) => v.email === email);
    const nom = meta?.userName || email;
    this.nom.set(nom);
    this.initiale.set(nom.charAt(0).toUpperCase() || '?');
  }

  /** `valeur` vient de `(onChange)` : la saisie complete part toute seule, sans attendre le bouton. */
  async soumettre(valeur: string = this.nip): Promise<void> {
    const saisi = String(valeur ?? '');
    if (this.verification() || saisi.length !== LONGUEUR_NIP) return;
    this.verification.set(true);
    this.erreur.set(null);
    try {
      const r = await this.verrou.deverrouiller(saisi);
      if (r.statut === 'ok') return;
      this.nip = '';
      if (r.statut === 'coffre-perdu') {
        this.erreur.set(this.translate.instant('VERROU.VAULT_LOST') as string);
        setTimeout(() => void this.auth.logout(), 2000);
        return;
      }
      this.erreur.set(`${this.translate.instant('PIN.WRONG') as string} — ${r.restants} ${this.translate.instant('PIN.ATTEMPTS_LEFT') as string}`);
    } catch {
      this.nip = '';
      this.erreur.set(this.translate.instant('VERROU.ERROR') as string);
    } finally {
      this.verification.set(false);
    }
  }

  deconnecter(): void {
    void this.auth.logout();
  }
}
